import { Chip, Grid } from '@mui/material';
import { dateFormat, dateFormatLongDate } from '@nx-smartmonkey/shared/helpers';
import { RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';

import More3Points from '../../../assets/svg/More3Points';
import { PlanActionsMenu } from '../../../components/menus/PlanActionsMenu';
import * as routing from '../../../constants/Routing';
import { ProjectPlansTableColumn } from '../../../context/plan/tableColumns';
import { PlanFlat } from '../../../domain/plan/PlanFlat';
import { PlanStatus } from '../../../domain/plan/PlanStatus';
import { Project } from '../../../domain/project/Project';
import { rootNavigate } from '../../../helpers/CustomRouter';
import Text from '../../../react-components/Text';
import { AlignmentTypes, DataTableColumnProps } from '../../../react-components/data-table/DataTable';
import { colorByPlanStatus } from '../shared/PlanStatusIcon';
import { StatusBarComponent } from './PlanStatusBarComponent';

const MAXIMUM_CHARS_PER_LABEL = 44;

type PlannerPlansColumnsResponse = { [key: string]: any }[];

type PlannerPlansGetColumns = {
  activeProject: Project;
  tableColumns: Array<ProjectPlansTableColumn>;
  onDeletePlan?: (deletedPlans: Array<PlanFlat>) => void;
};

export const getPlannerPlansColumns = ({
  activeProject,
  tableColumns,
  onDeletePlan,
}: PlannerPlansGetColumns): PlannerPlansColumnsResponse => {
  const columns: Array<DataTableColumnProps> = [];

  tableColumns.forEach(({ displayable, identifier, sortable }: ProjectPlansTableColumn) => {
    if (displayable) {
      switch (identifier) {
        case `label`:
          columns.push({
            width: 400,
            accessorName: `label`,
            name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="planner.plans.table.label" />,
            sortable,
            formatter: ({ doc: plan }) => (
              <span
                onClick={() => {
                  rootNavigate(
                    routing.PLANNER.PLAN_DETAILS.OVERVIEW.replace(`:projectId`, activeProject.id).replace(
                      `:planId`,
                      plan.id
                    )
                  );
                }}
              >
                {plan.label && plan.label !== `` ? (
                  <>
                    {plan.label.length > MAXIMUM_CHARS_PER_LABEL ? (
                      <RoutalTooltip id="plan-id" placement="top" title={plan.label}>
                        <Text variant="body" style={{ cursor: `pointer` }}>
                          {`${plan.label.substring(0, MAXIMUM_CHARS_PER_LABEL)}...`}
                        </Text>
                      </RoutalTooltip>
                    ) : (
                      <Text variant="body" style={{ cursor: `pointer` }}>
                        {plan.label}
                      </Text>
                    )}
                  </>
                ) : (
                  <Text
                    variant="body"
                    style={{ fontWeight: 500, cursor: `pointer`, color: RoutalPalette.secondary00 }}
                    intlId="no_name"
                  />
                )}
              </span>
            ),
          });
          break;
        case `status`:
          columns.push({
            width: 135,
            accessorName: `status`,
            sortable,
            align: `center` as AlignmentTypes,
            name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="planner.plans.table.status" />,
            formatter: ({ value: status }) =>
              status && (
                <Grid container alignItems="center">
                  <Chip
                    style={{
                      backgroundColor: colorByPlanStatus[status as PlanStatus],
                      fontSize: `10px`,
                      height: `26px`,
                      width: `120px`,
                    }}
                    label={<Text variant="body" style={{ color: `white` }} intlId={`planner.plans.status.${status}`} />}
                  />
                </Grid>
              ),
          });
          break;
        case `progress`:
          columns.push({
            accessorName: `progress`,
            align: `center` as AlignmentTypes,
            width: 120,
            name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="planner.plans.table.progress" />,
            formatter: ({ doc: plan }) => (
              <StatusBarComponent
                planId={plan.id}
                pendingStops={plan.pending_stops ?? 0}
                incompleteStops={plan.incomplete_stops ?? 0}
                completedStops={plan.completed_stops ?? 0}
                canceledStops={plan.canceled_stops ?? 0}
                width="100px"
              />
            ),
          });
          break;
        case `total_drivers`:
          columns.push({
            accessorName: `total_drivers`,
            align: `center` as AlignmentTypes,
            width: 60,
            name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="planner.plans.table.total_drivers" />,
            formatter: ({ value: totalRoutes }) => <Text>{totalRoutes}</Text>,
          });
          break;
        case `total_stops`:
          columns.push({
            accessorName: `total_stops`,
            align: `center` as AlignmentTypes,
            width: 60,
            name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="planner.plans.table.total_stops" />,
            formatter: ({ value: totalServices }) => <Text>{totalServices}</Text>,
          });
          break;
      }
    }
  });

  return [
    ...columns,
    ...(tableColumns.find(({ displayable, identifier }) => identifier === `execution_date` && displayable)
      ? [
          {
            width: 130,
            accessorName: `execution_date`,
            align: `center` as AlignmentTypes,
            sortable: false,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="planner.plans.table.execution_date" />
            ),
            formatter: ({ value: createdAt }) => (
              <RoutalTooltip title={dateFormat(createdAt)}>
                <Text variant="body">{dateFormatLongDate(createdAt)}</Text>
              </RoutalTooltip>
            ),
          } as DataTableColumnProps,
        ]
      : []),
    {
      accessorName: `id`,
      align: `right`,
      flex: 1,
      width: 60,
      formatter: ({ doc: plan, rowHover }) => (
        <Grid container justifyContent="flex-end" style={{ minHeight: `28px` }}>
          <PlanActionsMenu
            type="popover"
            showSettings={false}
            plans={[plan]}
            popoverProps={{
              hideArrow: true,
              content: <More3Points />,
              style: {
                visibility: rowHover ? `visible` : `hidden`,
                color: RoutalPalette.secondary60,
                padding: 0,
                margin: 0,
                border: `unset`,
              },
            }}
            onDeletePlan={onDeletePlan}
          />
        </Grid>
      ),
    },
  ] as Array<DataTableColumnProps>;
};
